/**
 * 16 环节时间轴：当前环节高亮、已完成环节打勾、未开始环节降噪。
 * 纯展示组件，不提供跳转（Phase 1 由教师端推进环节）。
 */
import { STAGE_SCHEDULE } from '@zongheng/core-schema';
import { Badge, cx } from '../ui/primitives.tsx';

export function StageTimeline({
  stageIndex,
  completedStages,
}: {
  stageIndex: number;
  completedStages: readonly number[];
}) {
  const completed = new Set(completedStages);
  return (
    <nav
      aria-label="赛程环节时间轴"
      data-testid="hud-timeline"
      className="flex items-stretch gap-1 overflow-x-auto rounded-xl border border-white/10 bg-bg-surface/70 p-2 backdrop-blur"
    >
      {STAGE_SCHEDULE.map((stage) => {
        const isCurrent = stage.index === stageIndex;
        const isDone = completed.has(stage.index);
        const state = isCurrent ? 'current' : isDone ? 'done' : 'pending';
        return (
          <div
            key={stage.index}
            data-testid={`hud-timeline-${stage.index}`}
            data-state={state}
            aria-current={isCurrent ? 'step' : undefined}
            title={`环节 ${stage.index} · ${stage.name}（${stage.speakerRole}）`}
            className={cx(
              'flex min-w-[96px] flex-1 flex-col gap-1 rounded-lg border px-2 py-1.5 transition-colors',
              isCurrent
                ? 'border-team-aff/60 bg-team-aff/10'
                : isDone
                  ? 'border-assert-pass/25 bg-assert-pass/5'
                  : 'border-white/5 bg-black/20',
            )}
          >
            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider">
              <span className={cx('mono', isCurrent ? 'text-team-aff' : 'text-slate-500')}>
                {String(stage.index).padStart(2, '0')}
              </span>
              {isCurrent && (
                <span className="text-team-aff" aria-label="当前环节">
                  ●
                </span>
              )}
              {!isCurrent && isDone && (
                <span className="text-assert-pass" aria-label="已完成">
                  ✓
                </span>
              )}
            </div>
            <div
              className={cx(
                'truncate text-[11px] font-medium',
                isCurrent ? 'text-slate-100' : isDone ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {stage.shortName}
            </div>
          </div>
        );
      })}
      {stageIndex === 0 && (
        <div className="flex items-center px-2 text-[11px] text-slate-500">
          <Badge tone="neutral">未开赛</Badge>
        </div>
      )}
    </nav>
  );
}

/** 环节阶段中文名 */
export const STAGE_PHASE_LABEL: Record<'PREP' | 'SPEAKING' | 'ENDED', string> = {
  PREP: '静默读卡',
  SPEAKING: '交锋中',
  ENDED: '本环节已结束',
};

/** 环节类型徽章文案 */
export function stageKindLabel(kind: string): string {
  switch (kind) {
    case 'CONSTRUCTIVE':
      return '申论';
    case 'CROSS_EXAM':
      return '单向质询';
    case 'DUEL':
      return '1v1 对辩';
    case 'FINAL_REBUTTAL':
      return '结辩';
    default:
      return kind;
  }
}
